import { Fragment, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import PageModal from '../common/PageModal'
import SimpleButton from '../common/SimpleButton'
import ToggleSwitch from '../common/ToggleSwitch'
import ConfirmationModal from '../common/ConfirmationModal'
import { branchesWritePermission } from '../../utils/permissionValidation'
import countries from '../../utils/countries.json'
import { TrashCanFull } from '../../icons'
import { removeBranchFromCorporation } from '../../actions/companies'
import './companies-management-modal.scss'

const ViewCompany = (props) => {
  const { t } = useTranslation()
  const { selectedCompany, handleEditCompany, closeModal } = props
  const [branches, setBranches] = useState(selectedCompany.branches || [])
  const [showRemoveFromCorporationConfirmModal, setShowRemoveFromCorporationConfirmModal] = useState(false)
  const [branchIdForAction, setBranchIdForAction] = useState(null)

  const companyHeaderContent = () => {
    return (
      <div className="status-wrapper">
        <div className="label">{t('status')}</div>
        <ToggleSwitch checked={selectedCompany.status === 'approved'} />
        <div className="status">{t([`company.${selectedCompany.status}`, selectedCompany.status])}</div>
      </div>
    )
  }

  const companyFooterContent = () => {
    return (
      <>
        <SimpleButton className="cancel-button" onClick={closeModal}>
          {t('close')}
        </SimpleButton>
        <SimpleButton className="action-button" onClick={handleEditCompany} requiredPermission={branchesWritePermission}>
          {t('edit')}
        </SimpleButton>
      </>
    )
  }

  const showRemoveConfirmModal = (branchId) => {
    setBranchIdForAction(branchId)
    setShowRemoveFromCorporationConfirmModal(true)
  }

  const handleRemoveFromCorporation = () => {
    setShowRemoveFromCorporationConfirmModal(false)
    props.actions.removeBranchFromCorporation(selectedCompany.id, branchIdForAction).then(() => {
      setBranches((prevBranches) => prevBranches.filter((branch) => branch.id !== branchIdForAction))
    })
  }

  return (
    <>
      <PageModal
        toggle
        onToggle={closeModal}
        className="companies-management-modal"
        title={t('details')}
        headerContent={companyHeaderContent()}
        footerContent={companyFooterContent()}
      >
        <div className="companies-management-modal-content">
          <div className="section-title">{t('general_information').toUpperCase()}</div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{t('organization_context.organization_type')}</p>
              <div className="value">{selectedCompany.isCorporation ? t('corporate_dso') : t('individual')}</div>
            </div>
            <div className="input-wrapper">
              <p className="input-option">{t('account_context.account_type')}</p>
              <div className="value">{selectedCompany.account}</div>
            </div>
          </div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{t('organization')}</p>
              <div className="value">{selectedCompany.companyName}</div>
            </div>
          </div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{t('admin')}</p>
              <div className="value">{selectedCompany.email}</div>
            </div>
          </div>
          <div className="separator" />
          <div className="section-title">{t('location').toUpperCase()}</div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{t('phone')}</p>
              <div className="value">{selectedCompany.telephone}</div>
            </div>
            <div className="input-wrapper">
              <p className="input-option">{t('country')}</p>
              <div className="value">{countries.find((c) => c.code === selectedCompany.country)?.name}</div>
            </div>
          </div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{t('address')}</p>
              <div className="value">{selectedCompany.officeAddressOne}</div>
            </div>
          </div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{t('city')}</p>
              <div className="value">{selectedCompany.city}</div>
            </div>
            <div className="input-wrapper">
              <p className="input-option">{t('state_province')}</p>
              <div className="value">{selectedCompany.stateProvinceRegion}</div>
            </div>
            <div className="input-wrapper">
              <p className="input-option">{t('zip_postal_code')}</p>
              <div className="value">{selectedCompany.zipPostalCode}</div>
            </div>
          </div>
          <div className="separator" />
          <div className="section-title">{t('organization_context.branches_single_multiple').toUpperCase()}</div>
          {branches.map((branch, index) => (
            <Fragment key={index}>
              {index !== 0 && <div className="inner-separator" />}
              <div className="row-wrapper">
                <div className="input-wrapper">
                  <p className="input-option">
                    {t('name')}
                    {selectedCompany.isCorporation && <TrashCanFull className="delete-icon " onClick={() => showRemoveConfirmModal(branch.id)} />}
                  </p>
                  <div className="value">{branch.branchName}</div>
                </div>
              </div>
              <div className="row-wrapper">
                <div className="input-wrapper">
                  <p className="input-option">{t('nav.members')}</p>
                  <div className="value">{branch.members}</div>
                </div>
              </div>
            </Fragment>
          ))}
        </div>
      </PageModal>
      {showRemoveFromCorporationConfirmModal && (
        <ConfirmationModal
          onToggle={() => setShowRemoveFromCorporationConfirmModal(false)}
          onAccept={() => handleRemoveFromCorporation()}
          onCancel={() => setShowRemoveFromCorporationConfirmModal(false)}
          message={t('organization_context.confirm_remove_from_corporation')}
          acceptButtonText={t('remove')}
          isWarning
          requiredPermission={branchesWritePermission}
        />
      )}
    </>
  )
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        removeBranchFromCorporation,
      },
      dispatch,
    ),
  }
}

export default connect(null, dispatchToProps)(ViewCompany)
